import {
  useInfiniteQuery,
  useQuery,
} from '@tanstack/react-query';

import TQConnectUserAPI from '../../../api/TQConnectUserAPI';
import {
  PROFILE_REVIEWS_LIMIT,
  PROFILE_SHOUTOUTS_LIMIT,
} from '../../../screens/TQConnect/constants';
import { useAppSelector } from '../../../shared/hooks';
import { QUERY_KEYS } from '../../../shared/queryKeys';
import { ITQProfileCompletionQuery } from '../../../types/tqConnectTypes';
import {
  ITQProfileInfo,
  ITQProfileReview,
  ITQProfileReviewsQuery,
  ITQProfileReviewsResult,
  ITQShoutout,
  ITQShoutoutsQuery,
  ITQShoutoutsResult,
} from '../../../types/tqConnectTypes/user';
import { OnboardingStatus } from '../../../types/userTypes';

/**
 * Custom hook to fetch TQ Connect profile info for a user
 *
 * @param data - The user ID and user type to fetch profile info for. { userId: string, userType: OnboardingStatus }
 * @param loadFromCache - Whether to load from cache (default: false)
 * @returns useQuery result with TQ Connect profile info data
 */
export const useTQConnectUserProfileInfo = (
  data: {
    userId: string;
    userType?: OnboardingStatus;
  },
  loadFromCache: boolean = false,
) => {
  const { userId, userType } = data;

  const {
    isLoggedIn,
    hostMetadata: { isCommunityFeatureActive },
  } = useAppSelector((state) => state.app);

  return useQuery({
    queryKey: QUERY_KEYS.TQ_CONNECT_USER_PROFILE_INFO(userId),
    queryFn: () => TQConnectUserAPI.getTQConnectProfileInfo(userId),
    enabled:
      isLoggedIn &&
      isCommunityFeatureActive &&
      Boolean(userId) &&
      userType === 'fan_completed',
    select: (resp) => resp.data.result as ITQProfileInfo,
    staleTime: loadFromCache ? Infinity : 0,
  });
};

/**
 * Custom hook to fetch TQ Connect profile completion queries
 *
 * @param loadFromCache - Whether to load from cache (default: false)
 * @returns useQuery result with profile completion queries data
 */
export const useTQProfileCompletionQueries = (
  loadFromCache: boolean = false,
) => {
  const {
    isLoggedIn,
    hostMetadata: { isCommunityFeatureActive },
  } = useAppSelector((state) => state.app);

  return useQuery({
    queryKey: QUERY_KEYS.TQ_CONNECT_PROFILE_COMPLETION_QUERIES,
    queryFn: () => TQConnectUserAPI.getProfileCompletionQueries(),
    enabled: isLoggedIn && isCommunityFeatureActive,
    select: (resp) =>
      resp.data.result.profileCompletionQueries as ITQProfileCompletionQuery[],
    staleTime: loadFromCache ? Infinity : 0,
  });
};

/**
 * Custom hook to fetch TQ Connect profile reviews with infinite scroll
 *
 * @param query - Query parameters for filtering (without page and limit)
 * @param enabled - Whether to enable the query (default: true)
 * @param loadFromCache - Whether to load from cache (default: false)
 * @returns useInfiniteQuery result with profile reviews data
 */
export const useTQConnectProfileReviews = (
  query: Omit<ITQProfileReviewsQuery, 'page' | 'limit'>,
  enabled: boolean = true,
  loadFromCache: boolean = false,
) => {
  const {
    isLoggedIn,
    hostMetadata: { isCommunityFeatureActive },
  } = useAppSelector((state) => state.app);

  return useInfiniteQuery({
    queryKey: QUERY_KEYS.TQ_CONNECT_PROFILE_REVIEWS(query),
    queryFn: ({ pageParam = 1 }) =>
      TQConnectUserAPI.getProfileReviews({
        ...query,
        page: pageParam,
        limit: PROFILE_REVIEWS_LIMIT,
      }),
    enabled: isLoggedIn && isCommunityFeatureActive && enabled,
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const lastPageResult = lastPage.data.result as ITQProfileReviewsResult;
      const currentItemsCount = allPages.reduce(
        (total, page) =>
          total + (page.data.result as ITQProfileReviewsResult).data.length,
        0,
      );

      // If we have more items to load, return next page number
      if (currentItemsCount < lastPageResult.total) {
        return allPages.length + 1;
      }

      // No more pages to load
      return undefined;
    },
    select: (data) => ({
      data: data.pages.reduce(
        (acc, page) => [...acc, ...page.data.result.data],
        [] as ITQProfileReview[],
      ),
      statistics: data.pages[0].data.result.statistics,
      total: data.pages[0].data.result.total,
      pageParams: data.pageParams,
    }),
    staleTime: loadFromCache ? Infinity : 0,
  });
};

/**
 * Custom hook to fetch TQ Connect profile shoutouts with infinite scroll
 *
 * @param query - Query parameters for filtering (without page and limit)
 * @param enabled - Whether to enable the query (default: true)
 * @param loadFromCache - Whether to load from cache (default: false)
 * @returns useInfiniteQuery result with profile shoutouts data
 */
export const useTQConnectProfileShoutouts = (
  query: Omit<ITQShoutoutsQuery, 'page' | 'limit'>,
  enabled: boolean = true,
  loadFromCache: boolean = false,
) => {
  const {
    isLoggedIn,
    hostMetadata: { isCommunityFeatureActive },
  } = useAppSelector((state) => state.app);

  return useInfiniteQuery({
    queryKey: QUERY_KEYS.TQ_CONNECT_PROFILE_SHOUTOUTS(query),
    queryFn: ({ pageParam = 1 }) =>
      TQConnectUserAPI.getProfileShoutouts({
        ...query,
        page: pageParam,
        limit: PROFILE_SHOUTOUTS_LIMIT,
      }),
    enabled: isLoggedIn && isCommunityFeatureActive && enabled,
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const lastPageResult = lastPage.data.result as ITQShoutoutsResult;
      const currentItemsCount = allPages.reduce(
        (total, page) =>
          total + (page.data.result as ITQShoutoutsResult).data.length,
        0,
      );

      // If we have more items to load, return next page number
      if (currentItemsCount < lastPageResult.total) {
        return allPages.length + 1;
      }

      // No more pages to load
      return undefined;
    },
    select: (data) => ({
      data: data.pages.reduce(
        (acc, page) => [...acc, ...page.data.result.data],
        [] as ITQShoutout[],
      ),
      total: data.pages[0].data.result.total,
      pageParams: data.pageParams,
    }),
    staleTime: loadFromCache ? Infinity : 0,
  });
};
